/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit'
import { createGenericSlice, GenericState } from '../lib'
import {
  getJobStatus,
  JobQueueResponse
} from '../../services/endpoints/jobQueue'

const sliceName = 'jobQueue'

export type JobQueueRecord = JobQueueResponse & {
  lookupId: string
}

export interface JobQueueState extends GenericState<JobQueueRecord> {}

export const jobQueueAdapter = createEntityAdapter<JobQueueRecord>({
  selectId: (job) => job.lookupId
})

export const fetchJobStatuses = createAsyncThunk(
  `${sliceName}/fetchJobStatuses`,
  async (jobIds: string[], { rejectWithValue }) => {
    const uniqueJobIds = Array.from(
      new Set(jobIds.map((id) => id.toString()))
    ).filter(Boolean)

    if (!uniqueJobIds.length) {
      return [] as JobQueueRecord[]
    }

    try {
      const responses = await Promise.all(
        uniqueJobIds.map(async (id) => {
          const job = await getJobStatus(id)

          if (job) {
            return {
              ...job,
              lookupId: id
            } as JobQueueRecord
          }

          const numericId = Number(id)

          return {
            id: Number.isNaN(numericId) ? id : numericId,
            _href: `/v1/job-queues/${id}`,
            arguments: null,
            dateCreated: '',
            device: null,
            finished: null,
            jobType: 'UNKNOWN',
            lastError: null,
            lastProcess: null,
            priority: 0,
            processOwner: null,
            runStatus: 'UNKNOWN',
            runType: 'DISTILLER',
            started: null,
            version: null,
            lookupId: id
          } as JobQueueRecord
        })
      )

      return responses
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

const thunkAdapters: any = [
  {
    adapterFn: jobQueueAdapter.upsertMany,
    thunk: fetchJobStatuses
  }
]

export const initialJobQueueState: JobQueueState = {
  loading: false,
  error: null,
  ids: [],
  entities: {}
}

export const jobQueueSlice = createGenericSlice({
  name: sliceName,
  initialState: initialJobQueueState,
  thunkAdapters
})

export const selectJobQueueState = (state: { jobQueue: JobQueueState }) =>
  state.jobQueue

export const jobQueueSelectors =
  jobQueueAdapter.getSelectors(selectJobQueueState)
